import { z } from "zod";
import { CLIENT_OUTDATED } from "./build.ts";

/**
 * The `code` of a `409` response body (`{ code, message }`), so clients can act on the cause
 * without parsing the message. `conflict` covers every other state conflict.
 * - `project_frozen`: the project is read-only; nothing will succeed until it is unfrozen;
 * - `turn_in_progress`: a reply is still being written in the conversation; retry later;
 * - `conversation_changed`: the conversation (or message) changed meanwhile; reload, then retry;
 * - `upload_already_confirmed`: the upload was confirmed already (a lost answer, say);
 * - `upload_missing`: no file was stored for the upload yet; send it, then confirm.
 * - `draft_stale`: the page changed since the draft was made; re-draft it (project-wiki);
 * - `draft_not_open`, `draft_not_stale`, `draft_changed`: the draft is not in the needed state;
 * - `wiki_title_taken`: another page has this title;
 * - `already_withdrawn`: the declassification was withdrawn already;
 * - `export_not_ready`: the export is still being built (or failed);
 * - `export_stale`: access to the exported pages changed since; export again.
 * - `reauth_required`: a security action needs a re-authentication within the last 5 minutes;
 * - `last_factor`: MFA is required and this is the account's last second factor.
 * - `totp_required`: a passkey needs the account's authenticator app as its other factor, and
 *   the account has none set up (#369).
 * - `entity_not_empty`: the entity still holds sub-entities, members, projects, groups or org
 *   codes; the body's `details` counts them (`EntityUsage`);
 * - `self_disable`: an admin cannot disable their own account;
 * - `last_tenant_admin`: the change would leave the tenant without an active tenant admin.
 * - `last_sign_in_method`: removing the IdP or releasing the domain would leave no active tenant
 *   admin a way to sign in (idp-domain-removal);
 * - `idp_inactive`: a federated sign-in came through an IdP the tenant removed or disabled.
 * - `approval_expired`, `approval_decided`: a tool call's approval expired or was decided
 *   already; ask again (agent-tools-platform);
 * - `approval_mismatch`: the stored call no longer matches the arguments its approval is bound to.
 * - `seat_limit`: the tenant's active users already fill its seat limit (spec `tenant-plans`);
 * - `storage_limit`: the file would take the tenant's stored bytes above its storage limit;
 * - `plan_in_use`: tenants are still assigned to the plan; `details.tenants` counts them;
 * - `self_mfa_reset`: an admin resets their own second factors from their account settings.
 * - `personal_project`: governance does not show or manage a personal project's members or
 *   settings; only its owner does.
 * - `not_standalone`: only a conversation outside any project can be moved, and only into one;
 * - `move_changed`: the move's effects changed since its preview; review them again;
 * - `residency_narrower`: the conversation was answered with global models and the target
 *   project is EU-only: it cannot move or be saved there.
 * - `access_request_pending`: the admin already has a pending access request for the project;
 * - `access_request_decided`: the access request was already decided, or it expired;
 * - `access_request_stale`: the requester may no longer make the change, or it would now open
 *   another project; the request stays pending until rejected or expired. For a held read: the
 *   held paths now give a higher role than the request names (a new request asks for it).
 * - `access_held`: the admin's read of the project is held (openspec change
 *   `governance-reparent-sync`), and the change would add access behind the hold.
 * - `fingerprint_linked`, `issue_exists`, `preview_changed` (in-app-feedback slice 3): the
 *   fingerprint is linked to another feedback group; the group already has an issue; the issue
 *   preview changed since the operator saw it (preview again before creating).
 * - `memory_user_active`: only a disabled user's memories can be erased (memory-governance);
 * - `memory_count_changed`: the user's memory count changed since it was shown; review it.
 * - `not_in_project`: a conversation outside any project cannot be shared with a project
 *   (openspec change `project-conversation-share`).
 * - `implications_changed`: a model consent named an implications version that is no longer the
 *   current one (the admin saw stale terms; model-policy-and-transparency).
 */
export const CONFLICT_CODES = [
  "rule_document_usage",
  "rule_project_usage",
  "attribute_rule_usage",
  "attribute_value_rule_usage",
  "change_set_closed",
  "change_set_changed",
  "item_stale",
  "too_many_open_change_sets",
  // agent-turn D11: a task proposal already decided, or past its 7 days.
  "proposal_decided",
  "proposal_expired",
  "page_already_in_change_set",
  // wiki-history-compare-revert: a restore of the page's current version would change nothing.
  "version_current",
  "conflict",
  "project_frozen",
  "personal_project",
  "turn_in_progress",
  "conversation_changed",
  "upload_already_confirmed",
  "upload_missing",
  "draft_stale",
  "draft_not_open",
  "draft_not_stale",
  "draft_changed",
  "wiki_title_taken",
  "already_withdrawn",
  "export_not_ready",
  "export_stale",
  "reauth_required",
  "last_factor",
  "totp_required",
  "entity_not_empty",
  "self_disable",
  "last_tenant_admin",
  "last_sign_in_method",
  "idp_inactive",
  "approval_expired",
  "approval_decided",
  "approval_mismatch",
  "seat_limit",
  "storage_limit",
  "plan_in_use",
  "self_mfa_reset",
  "not_standalone",
  "move_changed",
  "residency_narrower",
  "access_request_pending",
  "access_request_decided",
  "access_request_stale",
  "access_held",
  "memory_kind",
  "memory_queue_full",
  "memory_duplicate",
  "memory_user_active",
  "memory_count_changed",
  "implications_changed",
  // invite-link-copy: the user set a password (or was never invited), or is disabled, or has no
  // native account at the authorization server any more (an operator removed it).
  "invitation_not_pending",
  "user_disabled",
  "account_missing",
  // knowledge-folders: a sibling has the name (ignoring case), or the section holds 2,000.
  "name_taken",
  "folder_limit",
  // tasks: the project's `projects.tasks` setting is off, or the task holds 20 links already.
  "tasks_disabled",
  "link_limit",
  // conversation-attachments: the setting is off, a per-message or per-conversation cap, an
  // attachment that is not ready or already sent, an upload on an archived conversation.
  "attachments_disabled",
  "attachment_limit",
  "attachment_not_ready",
  "archived",
  "not_in_project",
  // standalone-attachments-private: the tenant scheme has no `named`-floor level, so a
  // standalone conversation cannot upload privately (fails closed).
  "private_level_unavailable",
  // An attachment already referenced by a message: its origin can no longer change; or a
  // project conversation's attachment, whose origin is fixed at upload.
  "attachment_sent",
  "origin_fixed",
  // artefacts-to-knowledge: the artefact is already in this project's knowledge, or the version
  // to update from is not newer than the one there.
  "already_added",
  "not_newer",
  // mcp-foundations-files: the target changed since the version the caller read
  // (`expectedVersion`); a folder that is not empty where only an empty one may be deleted.
  "version_mismatch",
  "folder_not_empty",
  // wiki-collaboration: a folder item names a folder deleted since it was proposed.
  "folder_missing",
  // wiki-collaboration: a working change set is its author's until submitted.
  "change_set_working",
  // wiki-collaboration: an open change request blocks approving the item or publishing the set.
  "change_requested",
  "fingerprint_linked",
  "issue_exists",
  "preview_changed",
  // model-connections-cross-account: enabling needs a fresh probe, and a confirmation when the
  // model account logs invocations.
  "model_connection_probe_required",
  "model_connection_logging_confirm",
  // model-picking D1, D2: the operator turned the provider kind off; the account already has
  // that binding.
  "model_provider_disabled",
  "model_account_same_binding",
  // tenant-admin-provisioning: an e-mail with an account or a user elsewhere. Operators get the
  // precise `admin_email_in_use`; everyone else the neutral `account_unavailable`, one answer
  // for every cause. An operator's MFA reset of an admin who never set a password, and a
  // resend in a tenant that is not active.
  "admin_email_in_use",
  "account_unavailable",
  "invitation_pending",
  "tenant_not_active",
  // wiki-resolve-with-agent: a run of the set is still in progress; the item changed after the
  // run, so Undo would discard a later edit.
  "resolution_running",
  "item_changed_since",
  // feedback-triage-assist: the suggestion was already decided (or its report or group moved on);
  // every report of a group to draft keeps its reports out of the assistant.
  "suggestion_decided",
  "assist_no_reports",
] as const;
export const conflictCodeSchema = z.enum(CONFLICT_CODES);
export type ConflictCode = z.infer<typeof conflictCodeSchema>;

/**
 * The `code` of a `403` response body when the refusal has a specific cause, e.g.
 * `standalone_disabled`: conversations outside a project are turned off for the user's entity.
 */
export const FORBIDDEN_CODES = [
  "cannot_review_hidden_items",
  // #152: a change set proposed through the caller's own MCP client needs another publisher
  // while the project's `wiki.mcpSelfPublish` is off.
  "self_publish_mcp_draft",
  "standalone_disabled",
  "memory_disabled",
  "project_memory_disabled",
  // wiki-suggestions: `wiki.suggestions` is off for the suggestion's project.
  "wiki_suggestions_disabled",
  // wiki-collaboration: the project's `wiki.trust` level does not let the caller decide.
  "wiki_trust_level",
  // in-app-feedback: the tenant turned `feedback.enabled` off.
  "feedback_disabled",
] as const;
export type ForbiddenCode = (typeof FORBIDDEN_CODES)[number];

/**
 * The `code` of a `422` response body when a domain rule refusal has a specific cause (otherwise
 * `unprocessable`):
 * - `nothing_to_declassify`: every intended reader can already read the sources;
 * - `empty_reason`: a declassification needs a reason;
 * - `readers_below_floor`: the readers are wider than the classification's readers floor allows.
 * - `model_roles_uncovered`: a `models.allowedModels` list leaves a needed role without a usable
 *   model at the written level or below; the body's `roles` names them.
 * - `too_deep`, `cycle`, `wrong_section` (knowledge-folders): a folder would nest deeper than 5
 *   levels or inside itself, or an item or folder would go into a folder of the other section.
 * - `not_a_member` (tasks): a task's assignee must hold a role in its project.
 * - `classification_not_private` (standalone-attachments-private): a standalone conversation's
 *   file must be at a `named`-floor level.
 * - `egress_floor_not_editable`: a written scheme sets or changes a level's `egressFloor`. Sent only
 *   by the release before (private-egress-floor slice 1); kept so the web can word it in a canary.
 * - `egress_floor_stricter_than_readers` (private-egress-floor): a level's `egressFloor` is
 *   stricter than its `readersFloor`; an egress floor may only loosen outbound tools.
 * - `logo_invalid` (tenant-branding): the uploaded logo is not an accepted image; the body's
 *   `reason` names why (`LOGO_INVALID_REASONS`).
 * - `federated_account` (invite-link-copy): the user's email domain signs in through an identity
 *   provider, so ESI issues no invitation link.
 * - `not_openable`, `too_large_to_view` (open-in-browser): the file's type has no Open action
 *   for that route (new tab or viewer), or a viewer file is larger than `VIEWER_MAX_BYTES`.
 * - `untested_lab_unacknowledged` (model-picking D4, D17): a tenant's `models.labs` adds a lab
 *   ESI has not tested without `acknowledgeUntested: true`; the body's `labs` names them.
 * - `issue_text_flagged`, `tracker_not_configured` (in-app-feedback slice 3): the operator's
 *   issue text looks copied from a report (preview it to see why); GitLab has no token yet.
 */
export const UNPROCESSABLE_CODES = [
  // sso-and-esi-credentials (#419, D1): a sign-in mode on a domain that is not verified and
  // bound to an active IdP.
  "domain_not_bound",
  "upload_size_limit",
  "unknown_models",
  "document_reader_required",
  "nothing_to_declassify",
  "empty_reason",
  "readers_below_floor",
  "model_roles_uncovered",
  "model_pin_disallowed",
  "untested_lab_unacknowledged",
  "too_deep",
  "cycle",
  "wrong_section",
  "not_a_member",
  "origin_derived",
  "classification_not_private",
  "egress_floor_not_editable",
  "egress_floor_stricter_than_readers",
  "logo_invalid",
  "federated_account",
  "not_openable",
  "too_large_to_view",
  // agent-turn D19: a file artefact downloads by a per-click link, never inline.
  "is_file",
  "wiki_item_invalid",
  "issue_text_flagged",
  // feedback-reporter-status D5: the "not planned" reason holds report text, an ID or a tenant.
  "reason_text_flagged",
  "tracker_not_configured",
  "anchor_not_found",
  // model-connections-cross-account: never the deployment account; the allowlist is full.
  "model_connection_same_account",
  "model_account_same_account",
  "model_account_capacity",
  // model-picking D2: an instance connection in an account bound to a tenant; a binding to a
  // tenant that is gone or being deleted.
  "model_account_tenant_bound",
  "model_account_tenant_unavailable",
] as const;
export type UnprocessableCode = (typeof UNPROCESSABLE_CODES)[number];

/** Structured refusal parameters, restricted to counts and public configuration labels. */
export type ErrorDetails = Record<string, number | string>;

export const INVALID_CODES = [
  "unknown_setting",
  "invalid_setting",
  "search_length",
  // idp-domain-removal: the typed confirmation does not match the IdP's name or the domain.
  "confirmation_mismatch",
] as const;
export type InvalidCode = (typeof INVALID_CODES)[number];

/**
 * `triage_budget_reached` (feedback-triage-assist D5): the instance's optional daily cap of the
 * triage assistant's spend is reached; set only when an operator sets a cap.
 */
export const QUOTA_CODES = [
  "model_budget",
  "user_budget",
  "throttled",
  "triage_budget_reached",
] as const;
export type QuotaCode = (typeof QUOTA_CODES)[number];

export const DOMAIN_ERROR_CODES = [
  "not_found",
  "forbidden",
  "conflict",
  "invalid",
  "unprocessable",
  "unsupported",
  "quota",
] as const;
export type DomainErrorCode = (typeof DOMAIN_ERROR_CODES)[number];

/**
 * The `503` body code of a request whose database connection could not be obtained or was lost
 * (openspec change `proxy-pinning-fix`), sent with `Retry-After`. The SPA repeats an idempotent
 * read once on exactly this code; `model_unavailable` and `schema_not_ready` are 503s too and are
 * not repeated.
 */
export const DATABASE_UNAVAILABLE = "database_unavailable";

/**
 * SQLSTATEs that mean the connection failed, not the statement: `08000` (RDS Proxy's borrow
 * timeout among others), `08001`, `08006`, `57P01` (`admin_shutdown`) and `57P03`
 * (`cannot_connect_now`: a new connection during a failover or restart).
 */
export const CONNECTION_SQLSTATES: readonly string[] = [
  "08000",
  "08001",
  "08006",
  "57P01",
  "57P03",
];

/** Transport refusals that do not originate in a domain rule. */
export const HTTP_ERROR_CODES = [
  CLIENT_OUTDATED,
  "unauthorized",
  "auth_unavailable",
  "internal_error",
  "model_unavailable",
  "schema_not_ready",
  DATABASE_UNAVAILABLE,
  "payload_too_large",
  "unsupported_media_type",
  "invalid_report",
  "forbidden_origin",
  "method_not_allowed",
  "unavailable",
] as const;
